// Personel drawer'ı — "Personel Ekle" (create) ve satır tıklanınca açılan
// personel kaydı (edit). Düzenleme yetkisi yoksa alanlar salt okunur, footer
// yalnız "Kapat". Silme işlemi listede değil, burada (gövdenin altında) durur.
// Alanlar kutusuz DrawerField + variant="standard" (design-principles §7).

import React from "react";
import { Box, Drawer, Typography } from "@mui/material";
import {
  Badge as BadgeIcon,
  PersonAdd as PersonAddIcon,
  DeleteOutline as DeleteIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  ActionButton,
  DrawerFooter,
  DrawerHeader,
  FormSelect,
  FormTextField,
  NameInput,
  PhoneInput,
  formatPhoneDisplay,
} from "../../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../../components/molecules";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import { GENDER_OPTIONS } from "./staffFormat";

const paperSx = {
  width: { xs: "100%", sm: 520 },
  bgcolor: COLORS.white,
  boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
  display: "flex",
  flexDirection: "column",
};

const gridSx = { display: "grid", rowGap: 2.5, columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } };

/** Salt okunur alan değeri — input'la aynı satır yüksekliği. */
const ReadValue = ({ children, mono }) => (
  <Typography
    sx={{
      fontSize: "0.875rem",
      color: children ? COLORS.slate800 : COLORS.slate300,
      py: 0.5,
      wordBreak: "break-word",
      ...(mono ? { fontVariantNumeric: "tabular-nums" } : {}),
    }}
  >
    {children || "—"}
  </Typography>
);

export default function StaffDrawer({
  open,
  mode = "edit",
  user,
  isManager = false,
  form,
  setField,
  roleTemplates,
  roleName,
  canEdit = true,
  canDelete = false,
  onDelete,
  onClose,
  onSubmit,
  loading,
  valid,
  palette,
}) {
  const isCreate = mode === "create";
  const editable = isCreate || canEdit;
  const inputProps = tileInputProps(true);
  const fullName = `${user?.name || ""} ${user?.surname || ""}`.trim();

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: paperSx }}>
      <DrawerHeader
        icon={isCreate ? <PersonAddIcon /> : <BadgeIcon />}
        title={isCreate ? "Personel Ekle" : fullName || "Personel"}
        subtitle={isCreate ? "Yeni personel hesabı oluşturun" : isManager ? "Klinik yöneticisi" : roleName}
        onClose={onClose}
      />

      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: COLORS.white, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isManager && !isCreate ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: COLORS.warningLightest,
              border: `1px solid ${COLORS.warningLight}`,
            }}
          >
            <StarIcon sx={{ fontSize: 16, color: COLORS.warning }} />
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.warningDarker }}>
              Klinik yöneticisi silinemez; bilgileri ve rolü güncellenebilir.
            </Typography>
          </Box>
        ) : null}

        <Box sx={gridSx}>
          <DrawerField label={editable ? "Ad *" : "Ad"}>
            {editable ? (
              <NameInput
                value={form.name}
                onChange={(v) => setField("name", v)}
                fullWidth size="small" variant="standard"
                inputProps={{ "aria-label": "Ad" }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue>{user?.name}</ReadValue>
            )}
          </DrawerField>
          <DrawerField label={editable ? "Soyad *" : "Soyad"}>
            {editable ? (
              <NameInput
                value={form.surname}
                onChange={(v) => setField("surname", v)}
                fullWidth size="small" variant="standard"
                inputProps={{ "aria-label": "Soyad" }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue>{user?.surname}</ReadValue>
            )}
          </DrawerField>

          {/* E-posta ve TC yalnız oluştururken girilir; sonra salt okunur */}
          <DrawerField label={isCreate ? "E-posta *" : "E-posta"} fullWidth>
            {isCreate ? (
              <FormTextField
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                fullWidth size="small" variant="standard"
                inputProps={{ "aria-label": "E-posta" }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue>{user?.email}</ReadValue>
            )}
          </DrawerField>

          <DrawerField label={editable ? "Cinsiyet *" : "Cinsiyet"}>
            {editable ? (
              <FormSelect
                value={form.gender}
                onChange={(v) => setField("gender", v)}
                options={GENDER_OPTIONS}
                placeholder="Seçiniz"
                variant="standard"
                palette={palette}
                SelectProps={{ displayEmpty: true, inputProps: { "aria-label": "Cinsiyet" } }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue>{user?.gender}</ReadValue>
            )}
          </DrawerField>
          <DrawerField label={editable ? "Telefon *" : "Telefon"}>
            {editable ? (
              <PhoneInput
                value={form.tellNo}
                onChange={(v) => setField("tellNo", v)}
                fullWidth size="small" variant="standard"
                inputProps={{ "aria-label": "Telefon" }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue mono>{formatPhoneDisplay(user?.tellNo)}</ReadValue>
            )}
          </DrawerField>

          <DrawerField label={isCreate ? "TC Kimlik No *" : "TC Kimlik No"}>
            {isCreate ? (
              <FormTextField
                value={form.tcNo}
                onChange={(e) => setField("tcNo", e.target.value.replace(/\D/g, "").slice(0, 11))}
                fullWidth size="small" variant="standard"
                inputProps={{ maxLength: 11, inputMode: "numeric", "aria-label": "TC Kimlik No" }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue mono>{user?.tcNo}</ReadValue>
            )}
          </DrawerField>
          {!isCreate ? (
            <DrawerField label="Hesap Durumu">
              <ReadValue>{user?.accountStatus}</ReadValue>
            </DrawerField>
          ) : null}

          <DrawerField label="Rol" fullWidth hint={editable ? "İzinler seçilen rol şablonundan gelir." : undefined}>
            {editable ? (
              <FormSelect
                value={form.roleTemplateId}
                onChange={(v) => setField("roleTemplateId", v)}
                options={roleTemplates}
                optionValue="_id"
                optionLabel="name"
                placeholder="Seçilmedi"
                variant="standard"
                palette={palette}
                SelectProps={{ displayEmpty: true, inputProps: { "aria-label": "Rol" } }}
                InputProps={inputProps}
              />
            ) : (
              <ReadValue>{roleName}</ReadValue>
            )}
          </DrawerField>
        </Box>

        {/* Silme — yalnız mevcut kayıtta, yetki varsa ve yönetici değilse */}
        {!isCreate && canDelete ? (
          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: `1px solid ${COLORS.slate100}`,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>Personeli sil</Typography>
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
                Hesap klinikten kaldırılır. Bu işlem geri alınamaz.
              </Typography>
            </Box>
            <ActionButton
              variant="delete"
              compact
              tone="soft"
              palette={palette}
              label="Sil"
              icon={<DeleteIcon sx={{ fontSize: 15 }} />}
              onClick={onDelete}
            />
          </Box>
        ) : null}
      </Box>

      {editable ? (
        <DrawerFooter
          onCancel={onClose}
          cancelLabel="Vazgeç"
          onSubmit={onSubmit}
          submitLabel={isCreate ? "Personel Ekle" : "Kaydet"}
          loadingLabel={isCreate ? "Oluşturuluyor..." : "Kaydediliyor..."}
          loading={loading}
          disabled={!valid}
          fullWidth
        />
      ) : (
        <DrawerFooter onCancel={onClose} cancelLabel="Kapat" fullWidth />
      )}
    </Drawer>
  );
}
